// src/App.js

import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";

// Global State Provider for Authentication
import { AuthProvider } from "./context/AuthContext";
import ScrollDownArrow from './components/shared/ScrollDownArrow';

// Components
import Navbar from "./components/Navbar";
import ProtectedRoute from './components/ProtectedRoute';

// Public Pages
import Homepage from './pages/Homepage';
import Services from './pages/Services';
import Authors from './pages/Authors';
import NotFound from './pages/NotFound';
import ThankYou from './pages/ThankYou';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelledPage from './pages/PaymentCancelledPage';
import Community from './pages/Community';
import DirectoryPage from './pages/DirectoryPage';
import ProfileDetailPage from './pages/ProfileDetailPage';
import SelfPublishingService from './pages/SelfPublishingService';
import Course from './pages/Course';
import Corporate from "./pages/Corporate";

// Özel Yönlendirme Sayfaları
import MeetSajeda from "./pages/MeetSajeda"; 
import Workshop1 from "./pages/Workshop1";
import Workshop2 from "./pages/Workshop2";
import Workshop3 from "./pages/Workshop3";
import SaminaBook from "./pages/SaminaBook"; 

// Membership Pages
import LoginPage from './membership/LoginPage';
import SignupPage from './membership/SubscriptionPage';
import ProfilePage from "./membership/ProfilePage";

// Admin Pages
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/AdminDashboard';
import AdminLogin from './admin/Login';
import ManageEvents from './admin/ManageEvents';
import ManageProfessionals from './admin/ManageProfessionals';
import AddEventForm from "./admin/AddEventForm";
import ManageCampaigns from "./admin/ManageCampaigns";
import CreateCampaign from "./admin/CreateCampaign";
import StatisticsPage from './admin/StatisticsPage';
import ManageOrdersPage from './admin/ManageOrdersPage';
import TaskBoard from './admin/manage-tasks/TaskBoard';
import ManageBookPages from './admin/ManageBookPages';
import BookLandingPage from './pages/BookLandingPage';

// User Management Pages
import UserListPage from './admin/manage-users/UserListPage';
import EditUserPage from './admin/manage-users/EditUserPage';
import AddUserPage from './admin/manage-users/AddUserPage'; 

import ContactPage from './pages/ContactPage';
import ManageMessages from './admin/ManageMessages';
// --- YENİ EKLENDİ ---
import ManageSubscribers from './admin/ManageSubscribers'; 

const AppLayout = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  if (isAdminRoute) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />

        {/* --- ROLE-BASED ROUTE PROTECTION --- */}
        <Route 
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['admin', 'editor', 'marketing', 'order']}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} /> 
          
          {/* Editor Role Routes */}
          <Route path="manage-events" element={<ProtectedRoute allowedRoles={['admin', 'editor']}><ManageEvents /></ProtectedRoute>} />
          <Route path="add-event" element={<ProtectedRoute allowedRoles={['admin', 'editor']}><AddEventForm /></ProtectedRoute>} />
          
          {/* Marketing Role Routes */}
          <Route path="manage-professionals" element={<ProtectedRoute allowedRoles={['admin']}><ManageProfessionals /></ProtectedRoute>} />
          <Route path="manage-campaigns" element={<ProtectedRoute allowedRoles={['admin', 'marketing']}><ManageCampaigns /></ProtectedRoute>} />
          <Route path="create-campaign" element={<ProtectedRoute allowedRoles={['admin', 'marketing']}><CreateCampaign /></ProtectedRoute>} />
          <Route path="manage-messages" element={<ProtectedRoute allowedRoles={['admin']}><ManageMessages /></ProtectedRoute>} />
          
          {/* YENİ EKLENDİ: Manage Subscribers Route */}
          <Route path="manage-subscribers" element={<ProtectedRoute allowedRoles={['admin', 'marketing']}><ManageSubscribers /></ProtectedRoute>} />
          
          {/* Order Role Route */}
          <Route path="manage-orders" element={<ProtectedRoute allowedRoles={['admin', 'order']}><ManageOrdersPage /></ProtectedRoute>} />
          <Route path="manage-tasks" element={<ProtectedRoute allowedRoles={['admin', 'editor','marketing', 'order']}><TaskBoard /></ProtectedRoute>} />
          
          {/* Admin-Only Route */}
          <Route path="statistics" element={<ProtectedRoute allowedRoles={['admin']}><StatisticsPage /></ProtectedRoute>} /> 
          <Route path="manage-users" element={<ProtectedRoute allowedRoles={['admin']}><UserListPage /></ProtectedRoute>} />
          <Route path="manage-users/edit/:userId" element={<ProtectedRoute allowedRoles={['admin']}><EditUserPage /></ProtectedRoute>} />
          <Route path="manage-users/add" element={<ProtectedRoute allowedRoles={['admin']}><AddUserPage /></ProtectedRoute>} />
          <Route 
            path="manage-book-pages" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'editor']}>
                <ManageBookPages />
              </ProtectedRoute>
            } 
          />
        </Route>
      </Routes>
    );
  }

  // --- PUBLIC ROUTES ---
  const isFullWidthPage = [
    "/", "/community", "/self-publishing-service", "/directory", "/contact", "/course", "/corporate", 
    "/meet-sajeda", "/workshop-1", "/workshop-2", "/workshop-3", "/samina-book"
  ].includes(location.pathname) || location.pathname.startsWith("/book/");
  
  const hasOwnContainer = ["/login", "/signup"].includes(location.pathname);
  const mainClassName = isFullWidthPage || hasOwnContainer ? "" : "mt-4 container";

  return (
    <div className="d-flex flex-column min-vh-100">
      <Navbar />
      <main className={`flex-grow-1 ${mainClassName}`}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Homepage />} />
          <Route path="/services" element={<Services />} />
          <Route path="/authors/*" element={<Authors />} />
          <Route path="/community" element={<Community />} />
          <Route path="/corporate" element={<Corporate />} />
          <Route path="/directory/:profileId" element={<ProfileDetailPage />} />
          <Route path="/directory" element={<DirectoryPage />} />
          <Route path="/self-publishing-service" element={<SelfPublishingService />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/cancelled" element={<PaymentCancelledPage />} />
          <Route path="/contact" element={<ContactPage />} /> 
          <Route path="/course" element={<Course />} />

          {/* Özel Landing Pageler */}
          <Route path="/meet-sajeda" element={<MeetSajeda />} />
          <Route path="/workshop-1" element={<Workshop1 />} />
          <Route path="/workshop-2" element={<Workshop2 />} />
          <Route path="/workshop-3" element={<Workshop3 />} />
          <Route path="/samina-book" element={<SaminaBook />} />
          
          {/* Membership Routes */}
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/book/:bookId" element={<BookLandingPage />} />
          
          {/* Catch-all Route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <ScrollDownArrow /> 
    </div>
  );
};

const App = () => (
  <HelmetProvider>
    <AuthProvider>
      <Router>
        <AppLayout />
      </Router>
    </AuthProvider>
  </HelmetProvider>
);

export default App;